import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SortableTable from '~/components/core/Tables/SortableTable';
import { Text } from '~/components/core/TextComponents';
import { PencilIcon, TrashIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import ConfigurationBreadcrumbs from '~/components/SystemConfiguration/IncidentConfigurationPage/ConfigurationBreadcrumbs';
import type { IncidentConfigurationSummary } from '~/components/SystemConfiguration/IncidentConfigurationPage/types';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import Switch from '~/components/ui/atoms/Switch';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateToLocal } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

import PolicyCategoryDialog from './PolicyCategoryDialog';
import type { PolicyCategoryRow } from './types';
import { policyCategoriesUrl } from './types';

interface PolicyCategoriesScreenProps {
  configurationSummary: IncidentConfigurationSummary;
  onBack: () => void;
}

const PolicyCategoriesScreen: React.FC<PolicyCategoriesScreenProps> = ({ configurationSummary, onBack }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const baseUrl = policyCategoriesUrl(organization.id, configurationSummary.id);

  const { isLoading, isError, data: categories, reloadData } = useDataFetcher<PolicyCategoryRow[]>(baseUrl);

  const [searchText, setSearchText] = useState('');
  const [dialogState, setDialogState] = useState<{ open: boolean; category: PolicyCategoryRow | null }>({
    open: false,
    category: null,
  });
  const [categoryToDelete, setCategoryToDelete] = useState<PolicyCategoryRow | null>(null);
  const [categoryToDisable, setCategoryToDisable] = useState<PolicyCategoryRow | null>(null);
  // Rows with a PATCH in flight. Without this a second click issues a second PATCH whose
  // completion order — not the last click — decides the stored value.
  const [pendingCategoryIds, setPendingCategoryIds] = useState<ReadonlySet<number>>(new Set());

  const filteredCategories = (categories || []).filter(
    ({ display_name, key }) =>
      display_name.toLowerCase().includes(searchText.toLowerCase()) ||
      key.toLowerCase().includes(searchText.toLowerCase())
  );

  const patchEnabled = async (category: PolicyCategoryRow, isEnabled: boolean) => {
    setPendingCategoryIds((pending) => new Set(pending).add(category.id));
    try {
      await axios.patch(`${baseUrl}/${category.id}`, { is_enabled: isEnabled });
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setPendingCategoryIds((pending) => {
        const next = new Set(pending);
        next.delete(category.id);
        return next;
      });
    }
  };

  const handleToggleEnabled = async (category: PolicyCategoryRow) => {
    // Disabling a category the FNOL configuration references silently hides every field
    // limited to it — confirm instead of flipping quietly. Enabling (or toggling an
    // unreferenced category) has no such blast radius.
    if (category.is_enabled && category.is_referenced) {
      setCategoryToDisable(category);
      return;
    }
    await patchEnabled(category, !category.is_enabled);
  };

  const handleConfirmedDisable = async () => {
    if (!categoryToDisable) return;
    const category = categoryToDisable;
    setCategoryToDisable(null);
    await patchEnabled(category, false);
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    // Close before awaiting, as `handleConfirmedDisable` does: ConfirmModal leaves its
    // buttons live unless the caller drives `isSubmitting`, so a modal still open across
    // the request takes a second click and fires a second DELETE — which 404s on the
    // now-removed row and reports an error for a delete that succeeded.
    const category = categoryToDelete;
    setCategoryToDelete(null);
    try {
      await axios.delete(`${baseUrl}/${category.id}`);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const columns = [
    { id: 'display_name', label: 'Name' },
    { id: 'key', label: 'Key' },
    {
      id: 'last_updated_on',
      label: 'Last Updated',
      specialCell: ({ last_updated_on }: PolicyCategoryRow) => serverDateToLocal(last_updated_on),
    },
    { id: 'last_updated_by_user_name', label: 'Updated By' },
    {
      id: 'is_enabled',
      label: 'Enable',
      disableSort: true,
      specialCell: (row: PolicyCategoryRow) => (
        <Switch
          checked={row.is_enabled}
          disabled={pendingCategoryIds.has(row.id)}
          onChange={() => handleToggleEnabled(row)}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      width: '90px',
      specialCell: (row: PolicyCategoryRow) => (
        <div className="flex flex-row">
          <InlineIconButton
            icon={PencilIcon}
            tooltipTitle="Edit"
            // @ts-expect-error InlineIconButton is an untyped JSX module, so its optional props are
            // inferred as the `undefined` defaults it destructures. Fix the types when the component
            // is converted.
            ariaLabel="Edit category"
            onClick={() => setDialogState({ open: true, category: row })}
          />
          <InlineIconButton
            icon={TrashIcon}
            // Deleting a referenced key would break the configuration's own validation on
            // its next save, so the BE refuses it; disable up front instead of letting the
            // click discover the rejection.
            disabled={row.is_referenced}
            tooltipTitle={
              row.is_referenced
                ? 'This category is in use by the configuration. Remove the references first, or disable it.'
                : 'Delete'
            }
            // @ts-expect-error see the `ariaLabel` note above.
            ariaLabel="Delete category"
            onClick={() => setCategoryToDelete(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-12">
        <ConfigurationBreadcrumbs
          externalAdminRoute="/policy_configuration"
          configurationName="Policy Configuration"
          onCancel={onBack}
        />
      </div>
      <CardDialog title={`Policy Categories - ${configurationSummary.display_name}`}>
        <Text variant="small" weight="regular">
          Categories are reusable groupings defined by a condition over policy fields. Reference them from the FNOL
          configuration to show or require fields only when the claim&apos;s policy matches.
        </Text>
        <div className="flex flex-col gap-16">
          <div className="my-4 w-1/4">
            <TextField
              fullWidth
              value={searchText}
              onChange={(value: string) => setSearchText(value)}
              placeholder="Search Categories"
            />
          </div>
          <div className="flex justify-between">
            <Text weight={Text.WEIGHTS.SEMI_BOLD} variant={Text.VARIANTS.SM}>
              {`${filteredCategories.length} ${filteredCategories.length === 1 ? 'Category' : 'Categories'}`}
            </Text>
            <Button color="primary" onClick={() => setDialogState({ open: true, category: null })}>
              + Add Category
            </Button>
          </div>
        </div>
        <LoadingSwitch isLoading={isLoading} isError={isError}>
          <SortableTable columns={columns} rows={filteredCategories} stickyHeader maxHeight="60vh" />
        </LoadingSwitch>
      </CardDialog>

      {dialogState.open && (
        <PolicyCategoryDialog
          categoriesUrl={baseUrl}
          emptyCatalogMessage="No policy fields available for this configuration."
          category={dialogState.category}
          onClose={() => setDialogState({ open: false, category: null })}
          onSaved={reloadData}
        />
      )}

      <ConfirmModal
        isOpen={!!categoryToDelete}
        title="Delete Category?"
        contentText={`The category "${categoryToDelete?.display_name ?? ''}" will be deleted. This cannot be undone.`}
        primaryButtonName="Delete"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleDelete}
        onClose={() => setCategoryToDelete(null)}
      />

      <ConfirmModal
        isOpen={!!categoryToDisable}
        title="Disable Category?"
        contentText={`"${categoryToDisable?.display_name ?? ''}" is referenced by this configuration's FNOL fields. Disabling it will hide every field limited to it until it is enabled again. Existing configuration references are kept.`}
        primaryButtonName="Disable"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleConfirmedDisable}
        onClose={() => setCategoryToDisable(null)}
      />
    </>
  );
};

export default PolicyCategoriesScreen;
